// Admin → 보안 정책 — IP 화이트리스트, IP 차단 목록, 보안 이벤트, 정책 설정.
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { api } from "../../../shared/api/client";
import { Empty, Loading } from "../../../shared/components/Empty";
import { Pill } from "../../../shared/components/Pill";
import { I } from "../../../shared/Icon";
import { toast } from "../../../shared/stores/toasts";

// ── Types ─────────────────────────────────────────────────────────────────────

type IpRange = { id: number; cidr: string; description: string | null; policy: string; is_active: boolean };

type BlockEntry = {
  id: number; ip: string;
  country_code: string | null; country_name: string | null;
  reason: string | null; auto_blocked: boolean;
  blocked_at: string; expires_at: string | null; unblocked_at: string | null;
  notes: string | null;
};

type SecurityEvent = {
  id: number; ip: string;
  country_code: string | null; event_type: string;
  detail: string | null; user_agent: string | null;
  created_at: string;
};

type Policy = {
  geo_block_enabled: boolean;
  allowed_countries: string[];
  threshold: number;
  window_minutes: number;
  block_duration_hours: number;
};

// ── Sub-tab helpers ───────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange }: {
  tabs: { id: string; l: string }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 4, background: "var(--panel)", borderRadius: 8, padding: 3, alignSelf: "flex-start", flexWrap: "wrap" }}>
      {tabs.map((t) => (
        <button key={t.id} onClick={() => onChange(t.id)}
          style={{
            padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
            background: active === t.id ? "var(--surface)" : "transparent",
            color: active === t.id ? "var(--text)" : "var(--text-3)",
            border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
            boxShadow: active === t.id ? "var(--shadow-sm)" : "none",
          }}>{t.l}</button>
      ))}
    </div>
  );
}

function FR({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "11px 18px", borderTop: "1px solid var(--border)", gap: 12 }}>
      <span style={{ width: 180, fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>{children}</div>
    </div>
  );
}

const EVENT_COLOR: Record<string, string> = {
  GEO_BLOCK_ATTEMPT: "warn",
  AUTO_BLOCKED:      "danger",
  MANUAL_BLOCKED:    "danger",
  UNBLOCKED:         "ok",
};

const EVENT_LABEL: Record<string, string> = {
  GEO_BLOCK_ATTEMPT: "해외 로그인 시도",
  AUTO_BLOCKED:      "자동 차단",
  MANUAL_BLOCKED:    "수동 차단",
  UNBLOCKED:         "차단 해제",
};

// ── IP Whitelist sub-tab ──────────────────────────────────────────────────────

function WhitelistTab() {
  const [items, setItems] = useState<IpRange[] | null>(null);
  const [form, setForm] = useState({ cidr: "", description: "", policy: "ALLOW" });

  const reload = () => api.get<IpRange[]>("/admin/security/ip-ranges").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);

  const add = async () => {
    try {
      await api.post("/admin/security/ip-ranges", form);
      toast.success("등록됨");
      setForm({ cidr: "", description: "", policy: "ALLOW" });
      reload();
    } catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };
  const del = async (id: number) => {
    if (!confirm("삭제하시겠습니까?")) return;
    await api.delete(`/admin/security/ip-ranges/${id}`);
    reload();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="t-card">
        <h3 style={{ margin: "0 0 12px", fontSize: 14 }}>IP 화이트리스트 추가</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8 }}>
          <input className="t-input" placeholder="CIDR (예: 192.168.0.0/24)" value={form.cidr} onChange={(e) => setForm({ ...form, cidr: e.target.value })}/>
          <input className="t-input" placeholder="설명" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
          <select className="t-input" value={form.policy} onChange={(e) => setForm({ ...form, policy: e.target.value })}>
            <option value="ALLOW">ALLOW</option>
            <option value="MFA_REQUIRED">MFA 필수</option>
          </select>
          <button className="t-btn t-btn-primary" onClick={add}>추가</button>
        </div>
      </div>
      {items === null && <Loading/>}
      {items?.length === 0 && <Empty msg="등록된 범위가 없습니다"/>}
      {items && items.length > 0 && (
        <div className="t-card" style={{ padding: 0 }}>
          <div className="t-list" style={{ padding: 16 }}>
            {items.map((r) => (
              <div key={r.id} className="t-row">
                <span className="mono">{r.cidr}</span>
                <span style={{ flex: 1 }}>{r.description}</span>
                <Pill kind={r.policy === "ALLOW" ? "ok" : "warn"}>{r.policy}</Pill>
                <button className="t-btn t-btn-sm" onClick={() => del(r.id)}>삭제</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── IP Blocklist sub-tab ──────────────────────────────────────────────────────

function BlocklistTab() {
  const [items, setItems] = useState<BlockEntry[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [addForm, setAddForm] = useState({ ip: "", reason: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const reload = () =>
    api.get<BlockEntry[]>(`/admin/security/blocklist?include_unblocked=${showAll}`)
       .then((r) => setItems(r.data))
       .catch(() => setItems([]));

  useEffect(() => { reload(); }, [showAll]);

  const unblock = async (id: number, ip: string) => {
    if (!confirm(`${ip} 차단을 해제하시겠습니까?`)) return;
    try { await api.delete(`/admin/security/blocklist/${id}`); toast.success("차단 해제됨"); reload(); }
    catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };

  const manualBlock = async () => {
    if (!addForm.ip || !addForm.reason) { toast.error("IP와 사유를 입력해주세요"); return; }
    setAdding(true);
    try {
      await api.post("/admin/security/blocklist", addForm);
      toast.success(`${addForm.ip} 차단됨`);
      setAddForm({ ip: "", reason: "", notes: "" });
      reload();
    } catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
    finally { setAdding(false); }
  };

  const activeCount = items?.filter((b) => !b.unblocked_at).length ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Summary banner */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {[
          { label: "현재 차단 IP",   val: activeCount,                            c: "var(--danger)" },
          { label: "자동 차단",       val: items?.filter((b) => b.auto_blocked && !b.unblocked_at).length ?? 0, c: "var(--warn)" },
          { label: "수동 차단",       val: items?.filter((b) => !b.auto_blocked && !b.unblocked_at).length ?? 0, c: "var(--info)" },
        ].map((s) => (
          <div key={s.label} className="t-card" style={{ padding: "12px 16px" }}>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.c, marginTop: 4 }} className="num">{s.val}</div>
          </div>
        ))}
      </div>

      {/* Manual block form */}
      <div className="t-card">
        <h3 style={{ margin: "0 0 12px", fontSize: 14 }}>수동 IP 차단</h3>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr auto", gap: 8 }}>
          <input className="t-input mono" placeholder="IP 주소" value={addForm.ip} onChange={(e) => setAddForm({ ...addForm, ip: e.target.value })}/>
          <input className="t-input" placeholder="차단 사유 (필수)" value={addForm.reason} onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })}/>
          <input className="t-input" placeholder="메모 (선택)" value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}/>
          <button className="t-btn t-btn-primary" onClick={manualBlock} disabled={adding} style={{ background: "var(--danger)", borderColor: "var(--danger)" }}>
            {adding ? "처리 중…" : "차단"}
          </button>
        </div>
      </div>

      {/* Blocklist table */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>차단 목록</strong>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, marginLeft: "auto", cursor: "pointer" }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}/>
          해제된 항목 포함
        </label>
      </div>

      {items === null && <Loading/>}
      {items?.length === 0 && <Empty msg="차단된 IP가 없습니다"/>}
      {items && items.length > 0 && (
        <div className="t-card" style={{ padding: 0, overflow: "auto" }}>
          <div style={{ display: "flex", padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", background: "var(--panel)", minWidth: 800 }}>
            <span style={{ width: 150 }}>IP 주소</span>
            <span style={{ width: 100 }}>국가</span>
            <span style={{ width: 60 }}>유형</span>
            <span style={{ flex: 1 }}>사유</span>
            <span style={{ width: 130 }}>차단 시각</span>
            <span style={{ width: 80 }}>만료</span>
            <span style={{ width: 70 }}>상태</span>
            <span style={{ width: 50 }}/>
          </div>
          {items.map((b) => (
            <div key={b.id} style={{ display: "flex", padding: "11px 16px", borderTop: "1px solid var(--border)", fontSize: 12.5, alignItems: "center", minWidth: 800, background: b.unblocked_at ? "var(--panel)" : undefined, opacity: b.unblocked_at ? 0.55 : 1 }}>
              <span style={{ width: 150 }} className="mono">{b.ip}</span>
              <span style={{ width: 100 }}>
                {b.country_code && (
                  <span className="t-pill muted mono" style={{ fontSize: 10 }}>{b.country_code}</span>
                )}
                <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 4 }}>{b.country_name}</span>
              </span>
              <span style={{ width: 60 }}>
                <span className={`t-pill ${b.auto_blocked ? "warn" : "info"}`} style={{ fontSize: 10 }}>
                  {b.auto_blocked ? "자동" : "수동"}
                </span>
              </span>
              <span style={{ flex: 1, fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.reason}</span>
              <span style={{ width: 130, fontSize: 11, color: "var(--text-3)" }} className="mono">
                {new Date(b.blocked_at).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
              <span style={{ width: 80, fontSize: 11, color: "var(--text-3)" }}>
                {b.expires_at ? new Date(b.expires_at).toLocaleDateString("ko-KR") : "영구"}
              </span>
              <span style={{ width: 70 }}>
                <span className={`t-pill ${b.unblocked_at ? "muted" : "danger"}`}>
                  {b.unblocked_at ? "해제됨" : "차단"}
                </span>
              </span>
              <span style={{ width: 50, textAlign: "right" }}>
                {!b.unblocked_at && (
                  <button className="t-btn" style={{ height: 26, fontSize: 11, color: "var(--p-600)" }} onClick={() => unblock(b.id, b.ip)}>해제</button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Security Events sub-tab ───────────────────────────────────────────────────

function EventsTab() {
  const [events, setEvents] = useState<SecurityEvent[] | null>(null);
  const [limit, setLimit] = useState(100);

  const reload = () =>
    api.get<SecurityEvent[]>(`/admin/security/events?limit=${limit}`)
       .then((r) => setEvents(r.data))
       .catch(() => setEvents([]));

  useEffect(() => { reload(); }, [limit]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>해외 로그인 시도 · 자동/수동 차단 이벤트 실시간 기록</span>
        <button className="t-btn t-btn-outline" style={{ marginLeft: "auto", height: 30 }} onClick={reload}>
          새로 고침
        </button>
        <select className="t-input" style={{ width: 120, height: 30, fontSize: 12 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          <option value={50}>최근 50건</option>
          <option value={100}>최근 100건</option>
          <option value={500}>최근 500건</option>
        </select>
      </div>

      {events === null && <Loading/>}
      {events?.length === 0 && <Empty msg="보안 이벤트가 없습니다"/>}
      {events && events.length > 0 && (
        <div className="t-card" style={{ padding: 0, overflow: "auto" }}>
          <div style={{ display: "flex", padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", background: "var(--panel)", minWidth: 700 }}>
            <span style={{ width: 145 }}>발생 시각</span>
            <span style={{ width: 140 }}>IP 주소</span>
            <span style={{ width: 80 }}>국가</span>
            <span style={{ width: 120 }}>이벤트</span>
            <span style={{ flex: 1 }}>상세</span>
          </div>
          {events.map((e) => (
            <div key={e.id} style={{ display: "flex", padding: "9px 16px", borderTop: "1px solid var(--border)", fontSize: 12.5, alignItems: "center", minWidth: 700 }}>
              <span style={{ width: 145, fontSize: 11, color: "var(--text-3)" }} className="mono">
                {new Date(e.created_at).toLocaleString("ko-KR")}
              </span>
              <span style={{ width: 140 }} className="mono">{e.ip}</span>
              <span style={{ width: 80 }}>
                {e.country_code && (
                  <span className="t-pill muted mono" style={{ fontSize: 10 }}>{e.country_code}</span>
                )}
              </span>
              <span style={{ width: 120 }}>
                <span className={`t-pill ${EVENT_COLOR[e.event_type] ?? "muted"}`} style={{ fontSize: 10 }}>
                  {EVENT_LABEL[e.event_type] ?? e.event_type}
                </span>
              </span>
              <span style={{ flex: 1, fontSize: 11.5, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Policy settings sub-tab ───────────────────────────────────────────────────

function PolicyTab() {
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<Policy>("/admin/security/policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const r = await api.patch<Policy>("/admin/security/policy", policy);
      setPolicy(r.data);
      toast.success("정책 저장됨");
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message); }
    finally { setSaving(false); }
  };

  if (!policy) return <Loading/>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="t-card" style={{ padding: 0 }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>지역 기반 자동 차단 정책</h3>
            <p style={{ margin: "3px 0 0", fontSize: 11.5, color: "var(--text-3)" }}>
              허용 국가 이외의 IP에서 단기간 다중 로그인 시도 시 자동 차단
            </p>
          </div>
          <button className="t-btn t-btn-primary" onClick={save} disabled={saving} style={{ marginLeft: "auto" }}>
            {saving ? "저장 중…" : "저장"}
          </button>
        </div>

        <FR label="자동 차단 활성화">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={policy.geo_block_enabled} onChange={(e) => setPolicy({ ...policy, geo_block_enabled: e.target.checked })}/>
            {policy.geo_block_enabled
              ? <span style={{ color: "var(--ok)", fontWeight: 600 }}>활성 — 해외 IP 자동 차단 중</span>
              : <span style={{ color: "var(--text-3)" }}>비활성</span>}
          </label>
        </FR>

        <FR label="허용 국가 (ISO 코드)">
          <input
            className="t-input mono"
            style={{ maxWidth: 280 }}
            value={policy.allowed_countries.join(", ")}
            onChange={(e) => setPolicy({ ...policy, allowed_countries: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
            placeholder="KR, US, JP"
          />
          <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>쉼표로 구분 · 기본값: KR</span>
        </FR>

        <FR label="차단 임계값">
          <input
            type="number" min={1} max={20}
            className="t-input mono"
            style={{ width: 80 }}
            value={policy.threshold}
            onChange={(e) => setPolicy({ ...policy, threshold: Number(e.target.value) })}
          />
          <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>회</span>
          <span style={{ fontSize: 11.5, color: "var(--text-3)", marginLeft: 4 }}>슬라이딩 윈도우 내 로그인 시도 횟수</span>
        </FR>

        <FR label="측정 윈도우">
          <input
            type="number" min={1} max={60}
            className="t-input mono"
            style={{ width: 80 }}
            value={policy.window_minutes}
            onChange={(e) => setPolicy({ ...policy, window_minutes: Number(e.target.value) })}
          />
          <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>분</span>
        </FR>

        <FR label="차단 지속 시간">
          <input
            type="number" min={0} max={8760}
            className="t-input mono"
            style={{ width: 80 }}
            value={policy.block_duration_hours}
            onChange={(e) => setPolicy({ ...policy, block_duration_hours: Number(e.target.value) })}
          />
          <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>시간</span>
          <span style={{ fontSize: 11.5, color: "var(--text-3)", marginLeft: 4 }}>0 = 영구 차단</span>
        </FR>
      </div>

      {/* Info card */}
      <div className="t-card" style={{ padding: 16, background: "color-mix(in srgb, var(--info) 8%, transparent)", borderColor: "var(--info)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <I.AlertC size={16} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }}/>
          <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.7 }}>
            <strong>자동 차단 작동 원리</strong><br/>
            허용 국가 이외의 IP에서 로그인 엔드포인트(<code>/auth/login</code>)를 <strong>{policy.window_minutes}분</strong> 내에 <strong>{policy.threshold}회</strong> 이상 호출하면 해당 IP를 자동으로 차단합니다.
            사설·루프백 IP (10.x.x.x, 172.16.x.x, 192.168.x.x, 127.x.x.x)는 항상 허용됩니다.
            차단 후 <strong>{policy.block_duration_hours > 0 ? `${policy.block_duration_hours}시간` : "영구"}</strong> 뒤 자동 해제됩니다.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

const TABS = [
  { id: "blocklist",  l: "IP 차단 목록" },
  { id: "events",     l: "보안 이벤트" },
  { id: "policy",     l: "정책 설정" },
  { id: "whitelist",  l: "IP 화이트리스트" },
];

export function AdminPolicies() {
  const [tab, setTab] = useState("blocklist");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <TabBar tabs={TABS} active={tab} onChange={setTab}/>
      {tab === "blocklist" && <BlocklistTab/>}
      {tab === "events"    && <EventsTab/>}
      {tab === "policy"    && <PolicyTab/>}
      {tab === "whitelist" && <WhitelistTab/>}
    </div>
  );
}
